import { Link, useParams } from "react-router-dom";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal } from "../components/Reveal";
import { PagePhoto } from "../components/PagePhoto";
import { pagePhotos } from "../content/photos";
import { CTASection } from "../components/CTASection";
import NotFound from "./NotFound";
import { useLang, useLp, localeUrl } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useServices, useProblems, useGuides } from "../i18n/content";

export default function GuidePage() {
  const { slug } = useParams();
  const guides = useGuides();
  const services = useServices();
  const problems = useProblems();
  const c = useCopy("guidePage");
  const cr = useCopy("crumbs");
  const cm = useCopy("common");
  const lp = useLp();
  const lang = useLang();
  const guide = guides.find((g) => g.slug === slug);
  if (!guide) return <NotFound />;

  const crumbs = [
    { name: cr.home, path: lp("/") },
    { name: cr.guides, path: lp("/guias/") },
    { name: guide.title, path: lp(`/guias/${guide.slug}/`) },
  ];
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.h1,
    description: guide.metaDescription,
    dateModified: guide.updatedAt,
    datePublished: guide.updatedAt,
    inLanguage: lang === "en" ? "en" : "es-ES",
    author: { "@type": "Organization", name: "MideFinca", url: localeUrl(lang, "/") },
    publisher: { "@type": "Organization", name: "MideFinca", url: localeUrl(lang, "/") },
    mainEntityOfPage: localeUrl(lang, `/guias/${guide.slug}/`),
  };

  return (
    <>
      <SEO
        title={guide.seoTitle}
        description={guide.metaDescription}
        path={`/guias/${guide.slug}/`}
        type="article"
        image={guide.ogImage ? localeUrl("es", guide.ogImage) : undefined}
        jsonLd={[breadcrumbSchema(crumbs), articleSchema]}
      />
      <Breadcrumbs items={crumbs} />
      <article className="container-site pb-8" data-testid={`guide-page-${guide.slug}`}>
        <header className="pt-10 max-w-3xl">
          <p className="tech-label mb-4">{guide.category}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">{guide.h1}</h1>
          <p className="mt-4 font-mono text-xs text-slate-600 tracking-wider">
            {c.updated} {guide.updatedAt}
          </p>
        </header>
        <PagePhoto photo={pagePhotos[guide.slug]} className="mt-10 max-w-3xl" />
        <div className="max-w-3xl">
          {guide.sections.map((section, i) => (
            <Reveal key={i} className="mt-12">
              <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-5">{section.h2}</h2>
              {section.paragraphs.map((p, j) => (
                <p key={j} className="text-[15px] sm:text-base text-slate-700 leading-relaxed mb-4">{p}</p>
              ))}
              {section.list && (
                <ul className="space-y-3 mt-2">
                  {section.list.map((item, k) => (
                    <li key={k} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
                      <span className="font-mono text-xs text-terracotta mt-1 shrink-0">{String(k + 1).padStart(2, "0")}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
          <Reveal className="mt-14 border-t hairline pt-10">
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <h2 className="tech-label mb-4">{cm.relatedServices}</h2>
                <ul className="space-y-2.5">
                  {guide.relatedServices.map((s) => {
                    const rel = services.find((x) => x.slug === s);
                    return rel ? (
                      <li key={s}><Link to={lp(`/servicios/${s}/`)} className="link-underline text-[15px]">{rel.shortName}</Link></li>
                    ) : null;
                  })}
                </ul>
              </div>
              {guide.relatedProblems.length > 0 && (
                <div>
                  <h2 className="tech-label mb-4">{c.yourCase}</h2>
                  <ul className="space-y-2.5">
                    {guide.relatedProblems.map((p) => {
                      const rel = problems.find((x) => x.slug === p);
                      return rel ? (
                        <li key={p}><Link to={lp(`/problemas/${p}/`)} className="link-underline text-[15px]">{rel.shortName}</Link></li>
                      ) : null;
                    })}
                  </ul>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </article>
      <CTASection />
    </>
  );
}
